import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

export const SEARCH_DEBOUNCE_MS = 300;

interface IUseDebouncedSearchReturn {
  search: string;
  onSearchInput: (next: string) => void;
  resetSearch: () => void;
}

// Debounced text search with cancel-on-select and cancel-on-unmount. Lazy useRef
// init — its argument runs every render, so an inline _.debounce would rebuild.
export const useDebouncedSearch = (
  debounceMs: number = SEARCH_DEBOUNCE_MS
): IUseDebouncedSearchReturn => {
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof _.debounce> | null>(null);
  if (!debounceRef.current) {
    debounceRef.current = _.debounce((next: string) => setSearch(next), debounceMs);
  }
  useEffect(() => () => debounceRef.current?.cancel(), []);

  const onSearchInput = useCallback((next: string) => {
    debounceRef.current?.(next);
  }, []);

  const resetSearch = useCallback(() => {
    debounceRef.current?.cancel();
    setSearch('');
  }, []);

  return { search, onSearchInput, resetSearch };
};
